import {
  createRouting,
  getRoutings,
  updateRouting,
  getRoutingById,
  deleteRouting,
  updateRoutingState,
} from '@/services/api/routing/routing'
import { getStudentsByBuilding } from '@/services/api/student/student'
import { getDistrictById } from '@/services/api/district/district'
import {
  getStops,
  assignStudentToManualStop,
  unassignStudentFromRouting,
  unassignStudentsFromRouting,
} from '@/services/api/stop/stop'
import {
  getStudentById,
  getStudentsByPaginate,
} from '@/services/api/student/student'
import { notifyApiFailure } from '@/utils/apiError'
import Vue from 'vue'

function defaultRoutingSteps() {
  return {
    contractId: null,
    districtId: null,
    buildingIds: [],
    educationType: 'Regular',
    company: null,
    depots: [],
    shift: 'AM',
    grades: [],
    arriveAtBuilding: 0,
    days: [],
    startDate: null,
    endDate: null,
    studentList: [],
    additionalStudents: [],
    // Total riders inside the selected buildings (Network Mercury wizard's
    // "N Buildings · N Students" summary). Declared here so it is reactive.
    buildingStudentCount: 0,
  }
}

export default {
  namespaced: true,
  state: {
    currentGeometryData: [],
    routingType: 'fixed',
    allRoutesLoading: false,
    routingGroupName: '',
    refreshRouting: false,
    routings: [],
    routingSteps: defaultRoutingSteps(),
    module: {
      routeName: '',
      stops: [],
      buildings: [],
      depotOptions: [],
      buildingOptions: [],
      currentDistrict: null,
      currentDepot: null,
      routeLengthDeadHead: 0,
      routeLengthCustomer: 0,
      studentsCount: 0,
    },
    currentRouting: null,
    routingGroup: null,
    students: [], // students filtered by building ids for routing
  },
  mutations: {
    /**
     * The route editor works on module.stops, but the Routes panel tree renders
     * from the routings list. Without this, renaming a stop or moving it to a
     * new address only showed in the editor until the next refresh.
     *
     * splice (not index assignment) so Vue 2 sees the change.
     */
    UPDATE_ROUTING_STOP(state, stop) {
      if (!stop || stop.id == null) return
      for (const routing of state.routings || []) {
        const stops = routing.stops
        if (!stops) continue
        const index = stops.findIndex((s) => Number(s.id) === Number(stop.id))
        if (index === -1) continue
        stops.splice(index, 1, { ...stops[index], ...stop })
        return
      }
    },
    // Same reason as UPDATE_ROUTING_STOP, for a stop that was just created:
    // the Routes panel tree and a re-opened editor read routings[].stops, so
    // without this a newly added traffic row vanished until the next refresh.
    ADD_ROUTING_STOP(state, { routingId, stop }) {
      if (!stop || stop.id == null) return
      const routing = (state.routings || []).find(
        (r) => Number(r.id) === Number(routingId),
      )
      if (!routing) return
      if (!routing.stops) Vue.set(routing, 'stops', [])
      const exists = routing.stops.some(
        (s) => Number(s.id) === Number(stop.id),
      )
      if (!exists) routing.stops.push(stop)
    },
    SET_CURRENT_GEOMETRY_DATA(state, payload) {
      state.currentGeometryData = payload
    },
    SET_ALL_ROUTES_LOADING(state, loading) {
      state.allRoutesLoading = loading
    },
    CHANGE_ROUTING_STEPS(state, payload) {
      state.routingSteps[payload.name] = payload.value
    },
    SET_ROUTING_STEPS(state, payload) {
      // Merge so callers (e.g. PDF init) that omit keys like additionalStudents
      // do not leave undefined fields that break spread/iteration elsewhere.
      state.routingSteps = {
        ...defaultRoutingSteps(),
        ...payload,
      }
    },
    CHANGE_ROUTING_TYPE(state, payload) {
      state.routingType = payload
    },
    RESET_ROUTING_STEPS(state) {
      state.routingSteps = defaultRoutingSteps()
    },
    SET_MODULE(state, payload) {
      state.module[payload.name] = payload.value
    },
    SET_ROUTE_NAME(state, payload) {
      state.module.routeName = payload
    },
    SET_STUDENTS(state, payload) {
      state.students = payload
    },
    SET_ROUTING_GROUP_NAME(state, payload) {
      state.routingGroupName = payload
    },
    SET_CURRENT_DISTRICT(state, payload) {
      state.module.currentDistrict = payload
    },
    SET_REFRESH_ROUTING(state, payload) {
      setTimeout(() => {
        state.refreshRouting = payload
      }, 2000)
    },
    SET_CURRENT_ROUTING(state, payload) {
      state.currentRouting = payload
    },
    SET_STOPS(state, payload) {
      state.module.stops = payload
    },
    SET_STOP(state, payload) {
      state.module.stops[payload.index] = payload.stop
    },
    SET_CURRENT_DEPOT(state, payload) {
      state.module.currentDepot = payload
    },
    SET_ROUTING_GROUP(state, payload) {
      state.routingGroup = payload
    },
    SET_ROUTINGS(state, payload) {
      state.routings = payload
    },
    SET_MODULE_BUILDINGS(state, payload) {
      state.module.buildings = payload
    },
  },
  actions: {
    setCurrentGeometryData({ commit }, payload) {
      commit('SET_CURRENT_GEOMETRY_DATA', payload)
    },
    async getRoutings({ commit }, payload) {
      commit('SET_ALL_ROUTES_LOADING', true)
      return getRoutings(payload)
        .then((response) => {
          const raw = response.data && response.data.data
          const rows = Array.isArray(raw) ? raw : []
          // Sort the stops by index within each routing
          const sortedRoutings = rows.map((routing) => {
            return {
              ...routing,
              stops: [...(routing.stops || [])].sort((a, b) => a.index - b.index), // Sort stops by index
            }
          })

          commit('SET_ROUTINGS', sortedRoutings)
          commit('SET_ALL_ROUTES_LOADING', false)
          return response.data
        })
        .catch((error) => {
          console.error(error)
          commit('SET_ALL_ROUTES_LOADING', false)
        })
    },
    async getRoutingById({ commit }, payload) {
      return getRoutingById(payload)
        .then((response) => {
          const routing = response.data

          // Sort stops by index
          routing.stops.sort((a, b) => a.index - b.index)

          commit('SET_CURRENT_ROUTING', routing)
          return routing
        })
        .catch((error) => {
          console.error(error)
        })
    },
    async getStudentsByBuilding({ commit, state }, payload) {
      // TODO: need to be updated
      commit('SET_ALL_ROUTES_LOADING', true)
      const response = await getStudentsByBuilding(payload)
      if (response.data.data) {
        const buildingStudents = response.data.data
        const extra = state.routingSteps.additionalStudents
        const extraList = Array.isArray(extra) ? extra : []
        // Manually-added riders may also belong to a fetched building; keep
        // one pin per student.
        const seen = new Set(buildingStudents.map((s) => s.id))
        const merged = [
          ...buildingStudents,
          ...extraList.filter((s) => s && !seen.has(s.id)),
        ]
        commit('SET_STUDENTS', merged)
        commit('SET_ALL_ROUTES_LOADING', false)
        return buildingStudents
      }
      commit('SET_ALL_ROUTES_LOADING', false)
      return []
    },
    async createRouting({ commit }, payload) {
      return createRouting(payload)
        .then((response) => {
          commit('SET_CURRENT_ROUTING', response.data)
          return response.data
        })
        .catch((error) => {
          // A network error (API restarting) used to be logged only, so the
          // wizard silently stopped; the helper toasts it as a retryable outage.
          notifyApiFailure(commit, 'Route not created', error)
        })
    },
    async updateRouting({ commit }, payload) {
      return updateRouting(payload)
        .then((response) => {
          commit('SET_CURRENT_ROUTING', response.data)
          return response.data
        })
        .catch((error) => {
          notifyApiFailure(commit, 'Route not updated', error)
        })
    },
    async updateRoutingState({ commit }, payload) {
      return updateRoutingState(payload).then((response) => {
        commit('SET_REFRESH_ROUTING', true)
        return response.data
      })
    },
    async getCurrentDistrict({ commit }, payload) {
      return getDistrictById(payload)
        .then((response) => {
          // The read is scoped to the user's organization, so a district
          // outside it comes back as an empty 200. It is recorded as a
          // district with nothing in it rather than left blank.
          commit(
            'SET_CURRENT_DISTRICT',
            response.data || { id: payload, buildings: [], students: [] },
          )
        })
        .catch((error) => {
          throw error
        })
    },
    async deleteRouting({ commit }, payload) {
      return deleteRouting(payload)
        .then(() => {})
        .catch((error) => {
          console.error(error)
          throw error
        })
    },
    async assignStudentToManualStop({ commit, rootState }, payload) {
      try {
        const response = await assignStudentToManualStop(payload)

        // Refetch stops
        const res = await getStops({
          limit: -1,
          'filter.routing.id': rootState.routings.currentRouting.id,
        })
        // order by index
        res.data.data.sort((a, b) => a.index - b.index)
        commit('SET_STOPS', res.data.data)

        return response.data
      } catch (error) {
        console.error(error)
        throw error // Re-throw the error to handle it in the calling code if needed
      }
    },
    async unassignStudentFromRouting({ commit, state }, payload) {
      try {
        const response = await unassignStudentFromRouting(payload)

        // Refetch stops
        const res = await getStops({
          limit: -1,
          'filter.routing.id': payload.routingId,
        })

        // order by index
        res.data.data.sort((a, b) => a.index - b.index)
        commit('SET_STOPS', res.data.data)

        // get student
        const { data: student } = await getStudentById(payload.studentId)

        // update students
        const studentIndex = state.students.findIndex(
          (s) => s.id === student.id,
        )

        const temp = [...state.students]
        temp[studentIndex] = student
        commit('SET_STUDENTS', temp)

        return response.data
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async unassignStudentsFromRouting({ commit, state }, payload) {
      try {
        const response = await unassignStudentsFromRouting(payload)

        // Refetch stops
        const res = await getStops({
          limit: -1,
          'filter.routing.id': payload.routingId,
        })

        // order by index
        res.data.data.sort((a, b) => a.index - b.index)
        commit('SET_STOPS', res.data.data)

        // get students
        const {
          data: { data: students },
        } = await getStudentsByPaginate({
          limit: -1,
          'filter.id': `$in${payload.studentIds.join(',')}`,
        })

        // update students: replace
        const temp = [...state.students]
        students.forEach((student) => {
          const index = temp.findIndex((s) => s.id === student.id)
          temp[index] = student
        })
        commit('SET_STUDENTS', temp)
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    changeRoutingType(state, payload) {
      state.commit('CHANGE_ROUTING_TYPE', payload)
    },
    changeRoutingSteps(state, payload) {
      state.commit('CHANGE_ROUTING_STEPS', payload)
    },
    setModule(state, payload) {
      state.commit('SET_MODULE', payload)
    },
    resetModule(state) {
      state.commit('SET_MODULE', { name: 'stops', value: [] })
      state.commit('SET_MODULE', { name: 'routeName', value: '' })
      state.commit('SET_MODULE', { name: 'routeLengthDeadHead', value: 0 })
      state.commit('SET_MODULE', { name: 'routeLengthCustomer', value: 0 })
      state.commit('SET_MODULE', { name: 'studentsCount', value: 0 })
    },
    updateRoutingGroupName(state, payload) {
      state.commit('SET_ROUTING_GROUP_NAME', payload)
    },
    setRouteName(state, payload) {
      state.commit('SET_ROUTE_NAME', payload)
    },
    setStops(state, payload) {
      state.commit('SET_STOPS', payload)
    },
    addRoutingStop(state, payload) {
      state.commit('ADD_ROUTING_STOP', payload)
    },
    setCurrentDepot(state, payload) {
      state.commit('SET_CURRENT_DEPOT', payload)
    },
    setCurrentDistrict(state, payload) {
      state.commit('SET_CURRENT_DISTRICT', payload)
    },
    setRoutingSteps(state, payload) {
      state.commit('SET_ROUTING_STEPS', payload)
    },
    resetRoutingSteps(state) {
      state.commit('RESET_ROUTING_STEPS')
    },
    setCurrentRouting(state, payload) {
      state.commit('SET_CURRENT_ROUTING', payload)
    },
    setRoutingGroup(state, payload) {
      state.commit('SET_ROUTING_GROUP', payload)
    },
    setModuleBuildings(state, payload) {
      state.commit('SET_MODULE_BUILDINGS', payload)
    },
  },
  getters: {},
}
